import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, inr } from "@/lib/api";
import { StockMenu } from "@/components/WatchAdd";
import { Flame, Snowflake } from "lucide-react";

const Bar = ({ s, max, onOpen }) => {
  const up = s.perf >= 0;
  const h = Math.max(8, (Math.abs(s.perf) / (max || 1)) * 110);
  return (
    <button onClick={() => onOpen(s)} data-testid={`pulse-${s.sector.replace(/\W+/g, "-")}`}
      className="group flex flex-col items-center w-full min-w-0 focus:outline-none" title={`${s.adv} up · ${s.dec} down — click for stocks`}>
      <div className="h-[130px] w-full flex flex-col justify-end items-center">
        {up && <span className="text-[10px] font-mono font-bold text-blue-600 mb-1 whitespace-nowrap">+{s.perf}%</span>}
        {up && <div className="w-[70%] max-w-[40px] bg-gradient-to-t from-blue-700 to-blue-400 group-hover:from-blue-600 group-hover:to-sky-300 transition-colors" style={{ height: h }} />}
      </div>
      <div className="w-full border-t border-slate-600 py-1 text-center">
        <span className="block text-[9px] font-mono text-slate-800 leading-tight truncate px-0.5 group-hover:text-blue-600">{s.sector}</span>
        <span className="block text-[9px] font-mono text-slate-500">{s.adv}↑ {s.dec}↓</span>
        {s.index_change != null && (
          <span className={`block text-[9px] font-mono font-bold ${s.index_change >= 0 ? "text-sky-600" : "text-orange-600"}`}
            title={`${s.index_name} (official index)`} data-testid={`pulse-idx-${s.sector.replace(/\W+/g, "-")}`}>
            idx {s.index_change > 0 ? "+" : ""}{s.index_change}%
          </span>
        )}
      </div>
      <div className="h-[130px] w-full flex flex-col items-center">
        {!up && <div className="w-[70%] max-w-[40px] bg-gradient-to-b from-orange-500 to-red-600 group-hover:from-orange-400 transition-colors" style={{ height: h }} />}
        {!up && <span className="text-[10px] font-mono font-bold text-rose-600 mt-1 whitespace-nowrap">{s.perf}%</span>}
      </div>
    </button>
  );
};

function StockList({ title, rows, kind, navigate }) {
  const list = rows.filter((r) => r[kind].length);
  return (
    <div>
      <h4 className="text-[11px] font-mono font-bold tracking-widest text-blue-600 mb-1.5">{title} · {list.length}</h4>
      {!list.length && <p className="text-xs text-slate-500 font-mono">none</p>}
      <div className="space-y-1">
        {list.map((r) => (
          <StockMenu key={r.symbol} instrumentId={r.instrument_id} symbol={r.symbol}>
          <button data-testid={`pulse-stock-${kind}-${r.symbol}`}
            className="w-full text-left border border-slate-200 hover:border-blue-500 px-2 py-1.5 transition-colors">
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-sm font-semibold text-slate-900">{r.symbol}</span>
              <span className="font-mono text-xs">
                {r.price != null && <span className="text-slate-600 mr-2">{inr(r.price)}</span>}
                <span className={r.change_pct >= 0 ? "text-emerald-600" : "text-rose-600"}>{r.change_pct >= 0 ? "+" : ""}{r.change_pct}%</span>
              </span>
            </div>
            <div className="flex flex-wrap gap-1 mt-1">
              {r[kind].map((l) => <span key={l} className="text-[9px] font-mono px-1 border border-slate-300 text-slate-600">{l}</span>)}
            </div>
          </button>
          </StockMenu>
        ))}
      </div>
    </div>
  );
}

export const SectorPulse = ({ sectors }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const [data, setData] = useState(null);
  // bars = our F&O stocks' average move; the official index move is shown separately as "idx"
  const shown = [...(sectors || [])].filter((s) => s.avg_change != null).map((s) => ({ ...s, perf: s.avg_change })).sort((a, b) => b.perf - a.perf);
  const max = Math.max(...shown.map((s) => Math.abs(s.perf)), 0.01);
  const openSector = (s) => {
    setOpen(s); setData(null);
    Promise.all([
      api.get("/today/sector-list-stocks", { params: { sector: s.sector } }).then((r) => r.data).catch(() => ({ stocks: [] })),
      api.get("/today/poc-cross").then((r) => r.data).catch(() => null),
      api.get("/today/adr-value").then((r) => r.data).catch(() => null),
      api.get("/today/tfr-gold").then((r) => r.data).catch(() => null),
    ]).then(([lists, poc, power, gold]) => {
      const pick = (rows, sink, label, key) => (rows || []).filter((r) => r.sector === s.sector && (r.open_x || 0) >= 3).forEach((r) => {
        const e = (sink[r.symbol] ||= { symbol: r.symbol, instrument_id: r.instrument_id, price: r.ltp, change_pct: r.change_pct, [key]: [] });
        e[key].push(`${label} · open ${r.open_x}×`);
      });
      const pw = {}, gd = {};
      ["up", "down"].forEach((side) => pick(power?.lists?.[side], pw, side === "up" ? "▲ Power Scan" : "▼ Power Scan", "power"));
      ["swing", "intraday"].forEach((tab) => ["up", "down"].forEach((side) => pick(gold?.tabs?.[tab]?.[side], gd, `${side === "up" ? "▲" : "▼"} Gold ${tab === "swing" ? "Swing" : "Intraday"}`, "gold")));
      const names = { d_up: "▲ TFR D", d_down: "▼ TFR D", w_up: "▲ TFR W", w_down: "▼ TFR W" };
      const by = {};
      Object.entries(poc?.tf?.["5m"] || poc?.lists || {}).forEach(([k, rows]) => rows.filter((r) => r.sector === s.sector).forEach((r) => {
        const e = (by[r.symbol] ||= { symbol: r.symbol, instrument_id: r.instrument_id, price: r.ltp, change_pct: r.change_pct, poc: [] });
        e.poc.push(`${names[k]} ${r.cross_at}${r.big ? ` · BIG ${r.big}×` : ""}`);
      }));
      setData({ ...lists, poc: Object.values(by), power: Object.values(pw), gold: Object.values(gd) });
    });
  };
  if (!shown.length) return null;
  return (
    <div className="border border-slate-200 bg-white p-3" data-testid="sector-pulse">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xs font-mono font-bold tracking-widest text-slate-800 flex items-center gap-2">
          <Flame className="h-3.5 w-3.5 text-blue-600" /> SECTOR PERFORMANCE · BEST → WORST <Snowflake className="h-3.5 w-3.5 text-rose-600" />
        </h3>
        <span className="text-[10px] font-mono text-slate-500">bars = avg % move of our F&O stocks · idx = official Nifty sector index · click a sector for its stocks</span>
      </div>
      <div className="overflow-x-auto"><div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${shown.length}, minmax(44px, 1fr))` }}>
        {shown.map((s) => <Bar key={s.sector} s={s} max={max} onOpen={openSector} />)}
      </div></div>
      <Sheet open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto bg-white border-slate-200" data-testid="sector-pulse-sheet">
          <SheetHeader>
            <SheetTitle className="font-mono text-slate-900">
              {open?.sector} <span className={open?.perf >= 0 ? "text-blue-600" : "text-rose-600"}>{open?.perf >= 0 ? "+" : ""}{open?.perf}%</span>
            </SheetTitle>
            <p className="text-[11px] font-mono text-slate-500">{open?.adv} stocks up · {open?.dec} down · stocks below are in our Today or Scanner lists</p>
          </SheetHeader>
          {!data ? <p className="mt-6 text-xs font-mono text-slate-500" data-testid="sector-pulse-loading">Loading…</p> : (
            <div className="mt-4 space-y-5">
              <StockList title="POWER SCAN · OPENING 3×+" rows={data.power || []} kind="power" navigate={navigate} />
              <StockList title="TFR GOLD · OPENING 3×+" rows={data.gold || []} kind="gold" navigate={navigate} />
              <StockList title="TFR D CROSSES (5m)" rows={data.poc || []} kind="poc" navigate={navigate} />
              <StockList title="IN TODAY'S LISTS" rows={data.stocks} kind="today" navigate={navigate} />
              <StockList title="IN SCANNER LISTS" rows={data.stocks} kind="scanner" navigate={navigate} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
